import { Button } from '@ordr-as/umami-react/button';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { FC, useState } from 'react';
import usePrintReceipt from '../hooks/usePrintReceipt';
import useIntl from '../hooks/useIntl';
import { usePlacements, usePlacementsActions, useSelectedPlacementId } from '../context/PlacementsStore';
import type { MinimalPlacement } from '@ordr-as/ordr-service-types/epos';

interface Props {
  reference: string;
  onCloseDialog: () => void;
  terminalPrint?: {
    name: string;
    onPrint: () => void;
    isPending?: boolean;
  };
}

const PrintReceiptDialog: FC<Props> = ({ onCloseDialog, reference, terminalPrint }) => {
  const placements = usePlacements();
  const selectedPlacementId = useSelectedPlacementId();
  const { setSelectedPlacementId } = usePlacementsActions();
  const { printReceipt, isPending } = usePrintReceipt(onCloseDialog);
  const intl = useIntl();

  const [showAll, setShowAll] = useState<boolean>(selectedPlacementId === undefined);

  const handlePrintReceipt = (placement: MinimalPlacement) => {
    setSelectedPlacementId(placement.id);
    printReceipt(reference, placement.id);
  };

  const handleShowAll = () => {
    setShowAll(!showAll);
  };

  return (
    <Dialog
      className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
      isDisabledAction={isPending || terminalPrint?.isPending}
      title={intl.formatMessage({ id: 'receipt.print.dialogHeader' })}
      onClose={onCloseDialog}
    >
      {terminalPrint && (
        <div className="flex justify-between p-3 border-b border-b-grey-lightest items-center">
          <div className="flex flex-col">
            <span>{terminalPrint.name}</span>
            <span className="text-xs text-grey-dark">Payment terminal</span>
          </div>
          <div>
            <Button
              kind="secondary"
              onClick={terminalPrint.onPrint}
              size="small"
              className="inline-flex text-small text-nowrap rounded-full"
              isDisabled={isPending || terminalPrint.isPending}
            >
              {intl.formatMessage({ id: 'receipt.print.printAction' })}
            </Button>
          </div>
        </div>
      )}

      {placements
        .filter((placement) => showAll || placement.id === selectedPlacementId)
        .map((placement) => (
          <div key={placement.id} className="flex justify-between p-3 border-b border-b-grey-lightest items-center">
            <div>{placement.name}</div>
            <div>
              {!showAll && (
                <Button
                  kind="tertiary"
                  onClick={handleShowAll}
                  size="small"
                  className="inline text-small text-nowrap rounded-full mr-3"
                >
                  {intl.formatMessage({ id: 'receipt.print.change' })}
                </Button>
              )}
              <Button
                kind="secondary"
                onClick={() => handlePrintReceipt(placement)}
                size="small"
                className="inline-flex text-small text-nowrap rounded-full"
              >
                {intl.formatMessage({ id: 'receipt.print.printAction' })}
              </Button>
            </div>
          </div>
        ))}
    </Dialog>
  );
};

export default PrintReceiptDialog;
